import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getHomeFeatured } from "../lib/api";
import { CATEGORIES, formatMoney, formatMoneyDecimal } from "../lib/categories";
import { getProductImage } from "../lib/categoryImages";
import { getRecent, getOrders } from "../lib/localStore";
import StarRating from "../components/StarRating";
import SmartImg from "../components/SmartImg";
import TrendingBanner from "../components/TrendingBanner";
import { ArrowUpRight, Sparkles, History, Package, Bike } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    let mounted = true;
    getHomeFeatured()
      .then((data) => mounted && setFeatured(data))
      .catch(() => mounted && setFeatured({}))
      .finally(() => mounted && setLoading(false));
    // Load client-side history
    setRecent(getRecent());
    setOrders(getOrders());
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <main data-testid="home-page">
      <TrendingBanner />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-neutral-200">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-14 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-12 gap-6 lg:gap-10">
            <div className="col-span-12 lg:col-span-7 reveal">
              <span className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-3 py-1 text-xs font-medium text-neutral-700">
                <Sparkles size={14} strokeWidth={1.5} />
                Fictional storefront · 500+ imaginary products
              </span>
              <h1 className="font-display font-bold mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-neutral-900">
                Everything you can&apos;t
                <br />
                afford, in
                <span className="brand-red"> one place</span>.
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-neutral-600 leading-relaxed">
                Cars, houses, boats, flights to anywhere, and hundreds of everyday
                items you don&apos;t need &mdash; add to cart with reckless abandon.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/category/cars"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors duration-200"
                  data-testid="hero-cta-shop-cars"
                >
                  Shop Cars
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/food"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand text-white text-sm font-medium hover:opacity-90 transition-opacity duration-200"
                  data-testid="hero-cta-food"
                >
                  <Bike size={16} strokeWidth={1.75} />
                  Order Food Delivery
                </Link>
                <Link
                  to="/category/flights"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-neutral-300 text-neutral-900 text-sm font-medium hover:bg-neutral-100 transition-colors duration-200"
                  data-testid="hero-cta-flights"
                >
                  Book a Flight Somewhere Warm
                </Link>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4 reveal" style={{ animationDelay: "120ms" }}>
              {["cars", "houses", "boats", "hotels"].map((slug) => {
                const item = featured?.[slug]?.items?.[0];
                const label = CATEGORIES.find((c) => c.slug === slug)?.label || slug;
                return (
                  <Link
                    key={slug}
                    to={`/category/${slug}`}
                    className="group relative aspect-square rounded-xl overflow-hidden border border-neutral-200 bg-neutral-100"
                    data-testid={`hero-tile-${slug}`}
                  >
                    {item && (
                      <SmartImg
                        src={getProductImage(item)}
                        alt={label}
                        className="h-full w-full"
                        imgClassName="transition-transform duration-500 group-hover:scale-105"
                        loading="eager"
                      />
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <span className="font-display font-semibold text-white text-sm">
                        {label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Recently viewed */}
      {recent.length > 0 && (
        <section
          className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 border-b border-neutral-200"
          data-testid="recently-viewed-strip"
        >
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-neutral-900 inline-flex items-center gap-3">
                <History size={22} strokeWidth={1.75} />
                Recently viewed
              </h2>
              <p className="text-neutral-500 text-sm mt-1">
                Picking up where you left off.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {recent.slice(0, 6).map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="group border border-neutral-200 rounded-xl overflow-hidden hover-lift bg-white"
                data-testid={`recent-card-${p.id}`}
              >
                <div className="aspect-square bg-neutral-100 overflow-hidden">
                  <SmartImg
                    src={p.image}
                    alt={p.title}
                    className="h-full w-full"
                    imgClassName="transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-3">
                  <h3 className="font-display font-semibold text-xs text-neutral-900 line-clamp-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-1 font-display font-bold text-sm text-neutral-900">
                    {formatMoney(p.price)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Category Bento */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-neutral-900">
              Browse categories
            </h2>
            <p className="text-neutral-600 mt-2 text-sm">
              17 categories. Approximately zero real brands (except the cars).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {CATEGORIES.map((c) => {
            const preview = featured?.[c.slug]?.items?.[0];
            return (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group relative overflow-hidden rounded-xl border border-neutral-200 bg-white hover-lift"
                data-testid={`category-tile-${c.slug}`}
              >
                <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                  {preview && (
                    <SmartImg
                      src={getProductImage(preview)}
                      alt={c.label}
                      className="h-full w-full"
                      imgClassName="transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-display font-semibold text-sm text-neutral-900">
                    {c.label}
                  </h3>
                  <p className="text-[11px] text-neutral-500 mt-0.5 line-clamp-1">
                    {c.tagline}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured strips per category */}
      {!loading &&
        featured &&
        ["cars", "hotels", "electronics", "clothing"].map((slug) => {
          const bucket = featured[slug];
          if (!bucket) return null;
          return (
            <section
              key={slug}
              className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 border-t border-neutral-200"
              data-testid={`featured-strip-${slug}`}
            >
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-neutral-900">
                    Trending in {bucket.label}
                  </h2>
                  <p className="text-neutral-500 text-sm mt-1">{bucket.tagline}</p>
                </div>
                <Link
                  to={`/category/${slug}`}
                  className="text-sm font-medium text-neutral-900 hover:underline underline-offset-4"
                  data-testid={`featured-strip-link-${slug}`}
                >
                  See all →
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {bucket.items.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="group border border-neutral-200 rounded-xl overflow-hidden hover-lift bg-white"
                  >
                    <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                      <SmartImg
                        src={getProductImage(p)}
                        alt={p.title}
                        className="h-full w-full"
                        imgClassName="transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <StarRating value={p.rating} count={p.reviewCount} showValue size={12} />
                      <h3 className="mt-1.5 font-display font-semibold text-sm text-neutral-900 line-clamp-2">
                        {p.title}
                      </h3>
                      <p className="mt-1 font-display font-bold text-neutral-900">
                        {formatMoney(p.price)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

      {/* Recent orders (client-side history) */}
      {orders.length > 0 && (
        <section
          className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 border-t border-neutral-200"
          data-testid="recent-orders-strip"
        >
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-neutral-900 inline-flex items-center gap-3">
                <Package size={22} strokeWidth={1.75} />
                Your recent orders
              </h2>
              <p className="text-neutral-500 text-sm mt-1">
                Fictional purchases, fondly remembered. Stored on this device.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orders.slice(0, 6).map((o) => (
              <Link
                key={o.orderNumber}
                to={`/order/${o.orderNumber}`}
                className="flex items-center gap-4 border border-neutral-200 rounded-xl p-4 bg-white hover-lift"
                data-testid={`order-history-card-${o.orderNumber}`}
              >
                <div className="h-16 w-16 rounded-lg bg-neutral-100 overflow-hidden shrink-0">
                  {o.firstImage && (
                    <SmartImg
                      src={o.firstImage}
                      alt=""
                      className="h-full w-full"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                    Order
                  </div>
                  <div className="font-display font-semibold text-sm text-neutral-900 truncate">
                    #{o.orderNumber}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {new Date(o.createdAt).toLocaleDateString()} · {o.itemCount} item{o.itemCount === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="font-display font-bold text-neutral-900 whitespace-nowrap">
                  {formatMoneyDecimal(o.total)}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
